import fieldListData from './field-list-data';
import Vue from 'vue';

import { v4 as uuid } from 'uuid';
import { RelationshipUtils } from '../relationship/relationship-utils';

export default {
  namespaced: true,

  state: {
    entity: null,
    current: null,
    tab: 'basic',
    basicForm: null,
    propertyForm: null,
    relationForm: null,
  },

  getters: {
    current: (state) => state.current,

    list: (state) =>
      (state.entity && state.entity.fields) || [],

    get(state) {
      return (id, reload) => {
        let found = null;

        if (state.entity && state.entity.fields) {
          found = state.entity.fields.find(
            (i) => i.id === id,
          );
        }

        if (!found || !reload) {
          if (state.current && state.current.id === id) {
            found = state.current;
          }
        }

        return found;
      };
    },

    listForTable(state, getters, rootState, rootGetters) {
      return fieldListData.adaptFieldsForTable(
        getters.list,
        rootGetters,
      );
    },
  },

  mutations: {
    tab(state, payload) {
      state.tab = payload;
    },

    basicForm(state, payload) {
      state.basicForm = payload;
    },

    propertyForm(state, payload) {
      state.propertyForm = payload;
    },

    relationForm(state, payload) {
      state.relationForm = payload;
    },

    bindEntity(state, payload) {
      state.entity = payload;
    },

    orderUp(state, payload) {
      const { fieldsAndRelationships } = payload;

      fieldsAndRelationships.sort(
        (a, b) =>
          RelationshipUtils.selectOrder(a, state.entity) -
          RelationshipUtils.selectOrder(b, state.entity),
      );

      fieldsAndRelationships.forEach((field, index) => {
        RelationshipUtils.setOrder(
          field,
          state.entity,
          index + 1,
        );
      });

      const current = fieldsAndRelationships.find(
        (field) => field.id === payload.id,
      );

      if (!current) {
        return;
      }

      const previous = fieldsAndRelationships.find(
        (field) =>
          RelationshipUtils.selectOrder(
            field,
            state.entity,
          ) ===
          RelationshipUtils.selectOrder(
            current,
            state.entity,
          ) -
            1,
      );

      RelationshipUtils.setOrder(
        previous,
        state.entity,
        RelationshipUtils.selectOrder(
          previous,
          state.entity,
        ) + 1,
      );

      RelationshipUtils.setOrder(
        current,
        state.entity,
        RelationshipUtils.selectOrder(
          current,
          state.entity,
        ) - 1,
      );

      fieldsAndRelationships.sort(
        (a, b) =>
          RelationshipUtils.selectOrder(a, state.entity) -
          RelationshipUtils.selectOrder(b, state.entity),
      );

      Vue.set(state.entity, 'fields', state.entity.fields);
    },

    orderDown(state, payload) {
      const { fieldsAndRelationships } = payload;

      fieldsAndRelationships.sort(
        (a, b) =>
          RelationshipUtils.selectOrder(a, state.entity) -
          RelationshipUtils.selectOrder(b, state.entity),
      );

      fieldsAndRelationships.forEach((field, index) => {
        RelationshipUtils.setOrder(
          field,
          state.entity,
          index + 1,
        );
      });

      const current = fieldsAndRelationships.find(
        (field) => field.id === payload.id,
      );

      if (!current) {
        return;
      }

      const next = fieldsAndRelationships.find(
        (field) =>
          RelationshipUtils.selectOrder(
            field,
            state.entity,
          ) ===
          RelationshipUtils.selectOrder(
            current,
            state.entity,
          ) +
            1,
      );

      RelationshipUtils.setOrder(
        next,
        state.entity,
        RelationshipUtils.selectOrder(next, state.entity) -
          1,
      );

      RelationshipUtils.setOrder(
        current,
        state.entity,
        RelationshipUtils.selectOrder(
          current,
          state.entity,
        ) + 1,
      );

      fieldsAndRelationships.sort(
        (a, b) =>
          RelationshipUtils.selectOrder(a, state.entity) -
          RelationshipUtils.selectOrder(b, state.entity),
      );

      Vue.set(state.entity, 'fields', state.entity.fields);
    },

    persist(state) {
      const fields = state.entity.fields;

      const index = fields.findIndex(
        (field) => field.id === state.current.id,
      );

      const isMustSetOrder =
        fields.length &&
        fields[0].order &&
        !state.current.order;

      if (isMustSetOrder) {
        Vue.set(state.current, 'order', fields.length);
      }

      if (index > -1) {
        fields.splice(index, 1, state.current);
      } else {
        fields.push(state.current);
      }
    },

    new(state) {
      state.current = {
        id: uuid(),
        name: {
          singular: null,
          plural: null,
        },
        label: {
          singular: null,
          plural: null,
        },
        properties: {},
        relation: {},
        validations: [],
      };

      state.tab = 'basic';
    },

    edit(state, payload) {
      state.current = JSON.parse(JSON.stringify(payload));
      state.tab = 'basic';
    },

    delete(state, payload) {
      const fields = state.entity.fields;

      if (!fields || !fields.length) {
        return;
      }

      const index = fields.findIndex(
        (e) => e.id === payload.id,
      );

      if (index > -1) {
        fields.splice(index, 1);
      }
    },

    copy(state, payload) {
      const fields = state.entity.fields;

      if (!fields || !fields.length) {
        return;
      }

      const originalField = fields.find(
        (e) => e.id === payload.id,
      );

      const newField = JSON.parse(
        JSON.stringify(originalField),
      );
      newField.id = uuid();
      newField.name.singular =
        newField.name.singular + 'Copy';
      newField.label.singular =
        newField.label.singular + ' (Copy)';
      newField.label.plural =
        newField.label.plural + ' (Copy)';
      fields.push(newField);
    },

    clear(state) {
      state.current = null;
      state.tab = 'basic';
    },

    clearPropertiesRelationAndValidations(state) {
      if (!state.current) {
        return;
      }

      Object.assign(state.current, {
        properties: {},
        relation: {},
        validations: [],
      });
    },

    mergePropertiesAndRelationDefaults(
      state,
      { properties, relation },
    ) {
      state.current.properties = Object.assign(
        properties,
        state.current.properties,
      );
      state.current.relation = Object.assign(
        relation,
        state.current.relation,
      );
    },
  },

  actions: {
    new({ commit, getters }) {
      commit('new');
      commit(
        'builder/fieldValidationCurrent/bind',
        getters.current.validations,
        { root: true },
      );
    },

    edit({ commit, getters }, payload) {
      commit('edit', payload);
      commit(
        'builder/fieldValidationCurrent/bind',
        getters.current.validations,
        { root: true },
      );
    },

    clear({ commit }) {
      commit('clear');
      commit('builder/fieldValidationCurrent/bind', null, {
        root: true,
      });
    },

    setupPropertiesRelationAndValidations({
      commit,
      dispatch,
    }) {
      commit('clearPropertiesRelationAndValidations');
      dispatch('mergePropertiesAndRelationDefaults');
    },

    copy({ commit }, payload) {
      commit('copy', payload);
    },

    delete({ commit }, payload) {
      commit('delete', payload);
    },

    orderUp({ commit, rootGetters, state }, payload) {
      const fieldsAndRelationships = [
        ...(state.entity.fields || []),
        ...RelationshipUtils.selectFromEntity(
          state.entity,
          rootGetters['builder/relationshipCurrent/list'],
        ),
      ];

      const newPayload = Object.assign({}, payload, {
        fieldsAndRelationships,
      });

      commit('orderUp', newPayload);
    },

    orderDown({ commit, rootGetters, state }, payload) {
      const fieldsAndRelationships = [
        ...(state.entity.fields || []),
        ...RelationshipUtils.selectFromEntity(
          state.entity,
          rootGetters['builder/relationshipCurrent/list'],
        ),
      ];

      const newPayload = Object.assign({}, payload, {
        fieldsAndRelationships,
      });

      commit('orderDown', newPayload);
    },

    mergePropertiesAndRelationDefaults({
      commit,
      getters,
      rootGetters,
    }) {
      const defaults = {
        properties: {},
        relation: {},
      };

      const propertiesEnabled = rootGetters[
        'builder/fieldProperty/enabled'
      ](
        rootGetters['builder/propertyCurrent/current'],
        rootGetters['builder/entityCurrent/current'],
        getters.current,
      );

      if (propertiesEnabled) {
        defaults.properties = rootGetters[
          'builder/fieldProperty/defaults'
        ](getters.current);
      }

      const relationEnabled = rootGetters[
        'builder/fieldRelation/enabled'
      ](
        rootGetters['builder/propertyCurrent/current'],
        rootGetters['builder/entityCurrent/current'],
        getters.current,
      );

      if (relationEnabled) {
        defaults.relation = rootGetters[
          'builder/fieldRelation/defaults'
        ](getters.current);
      }

      commit(
        'mergePropertiesAndRelationDefaults',
        defaults,
      );
      commit(
        'builder/fieldValidationCurrent/bind',
        getters.current.validations,
        { root: true },
      );
    },

    save({ commit, dispatch }) {
      return dispatch('validate').then((valid) => {
        if (!valid) {
          return Promise.reject();
        }

        return commit('persist');
      });
    },

    validate({ state, commit }) {
      const validateBasicForm = new Promise((resolve) => {
        if (!state.basicForm) {
          resolve(true);
          return;
        }

        state.basicForm.validate((valid) => {
          resolve(valid);
        });
      });

      const validatePropertyForm = new Promise(
        (resolve) => {
          if (!state.propertyForm) {
            resolve(true);
            return;
          }

          state.propertyForm.validate((valid) => {
            resolve(valid);
          });
        },
      );

      const validateRelationForm = new Promise(
        (resolve) => {
          if (!state.relationForm) {
            resolve(true);
            return;
          }

          state.relationForm.validate((valid) => {
            resolve(valid);
          });
        },
      );

      return Promise.all([
        validateBasicForm,
        validatePropertyForm,
        validateRelationForm,
      ]).then((valids) => {
        const allValid = valids.every((v) => !!v);

        if (allValid) {
          return true;
        }

        if (!valids[0]) {
          commit('tab', 'basic');
          return false;
        }

        if (!valids[1]) {
          commit('tab', 'property');
          return false;
        }

        if (!valids[2]) {
          commit('tab', 'relation');
          return false;
        }

        commit('tab', 'basic');
        return false;
      });
    },
  },
};
